import { CONFIG, EMAIL_VALIDATION_ERROR_CODE, PC_PROCESSING, EXCEPTION_TYPES, AUTH } from "constants/index";
import { AppService, OnBoardingService } from "services";
import UserService from "services/userServices";
import queryString from 'query-string';
import { NotFoundPath } from 'utils/routes';
import TicketServices from "services/ticketServices";
import { PHONE_TYPE } from "constants/pcProcessingWorkFlowConstants";
import { formatPhoneNumberIntl, isValidPhoneNumber } from 'react-phone-number-input';
import * as microsoftTeams from '@microsoft/teams-js';
import ProvisioningServices from "services/provisioningServices";
import { CheckRoutesConfigProps, InternalRouteProps, RouteConfig } from "components/App/routes";
import { matchPath } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import mergeWith from "lodash/mergeWith";
import DateUtils from "./date";
import { IDevice } from "pages/ManageDevicesPage/types";
import { ICountryProps } from "components/DynamicForm/DynamicComponent/CustomComponent/addressCard";
import { byAlpha3 } from "iso-country-codes";

export const filterDataByArrayConfig = (data: any, config: any) => {
  const filteredData = data?.map((item: any) => {
    const matchingConfigItem = config.find((configItem: any) => {
      const { KEY, LABEL } = configItem;
      return item?.label === LABEL && item?.key === KEY;
    });

    if (matchingConfigItem) {
      return item;
    } else {
      return null;
    }
  }).filter(Boolean);

  return filteredData;
};

export const addDecodedScriptToHead = (encoded: string) => {
  if (!encoded) return;
  try {
    const decoded = atob(encoded);
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.text = decoded;
    document.head.appendChild(script);
  } catch (error) {
    console.log(error);
  }
};

export const checkNewJoinerByEmail = async (user_email: string) => {
  try {
    const checkResponse = await OnBoardingService.checkNewJoiner(user_email);
    if (checkResponse.request.status === 200) {
      return checkResponse.data;
    }
  } catch (error) {
    return {};
  }
};

export const getNewJoinerInfoByEmail = async (user_email: string) => {
  try {
    const checkResponse = await OnBoardingService.getNewJoinerInfo(user_email);
    if (checkResponse.request.status === 200) {
      const { data: persona } = await OnBoardingService.getListPersonaDefinition(checkResponse.data?.persona_definition);
      return { ...checkResponse.data, persona };
    }
  } catch (error) {
    return {};
  }
};

export const getLeaverInfoByEmail = async (user_email: string) => {
  try {
    const checkResponse = await OnBoardingService.getLeaverInfo(user_email);
    if (checkResponse.status === 200) {
      const { data: persona } = await OnBoardingService.getListPersonaDefinition(checkResponse.data?.persona_definition);
      return { ...checkResponse.data, persona };
    }
  } catch (error) {
    return {};
  }
};

export const getUserProfile = async (user_email: string, user_name?: string, queryUserByUserName?: boolean ) => {
  try {
    queryUserByUserName = queryUserByUserName??false;
    const userResponse = user_name && user_name !== "" && queryUserByUserName
      ? await UserService.getUserDetailByUserName(user_name)
      : await UserService.getUserDetailByEmail(user_email);
      
    let user_data: any = {};
    if (userResponse && userResponse.data && userResponse.data.length > 0) {
      let userData = userResponse.data[0];
      const moreInfo = {
        uid: userData.uid,
        name: userData.name ||`${userData.first_name} ${userData.last_name}`,
        user_name: userData.user_name,
        phone: userData.phone,
        first_name: userData.first_name,
        last_name: userData.last_name,
        employee_number: userData.employee_number,
        vip: userData.vip,
        photo: userData.photo,
        employee_type: userData.employee_type,
        mobile_phone: userData.mobile_phone,
        time_zone: userData.timeZone,
        avatar_name: getAvatarName(userData.name ||`${userData.first_name} ${userData.last_name}`),
        city: userData.city,
        state: userData.state,
        country: userData.country,
        department: userData.department,
        manager_email: userData?.manager_email ?? '',
        preferred_language: userData.preferred_language,
        manager_uid: userData?.manager_uid ?? '',
        manager_name: userData?.manager_name ?? '',
        manager_first_name: userData?.manager_first_name ?? '',
        manager_last_name: userData?.manager_last_name ?? '',
        location: userData.location,
        locationId: userData.locationId,
        email: userData.email,
        address: userData.address,
        last_verified: new Date(),
        chosen_language: userData.chosen_language,
        isManager: userData.isManager,
        cost_center: userData.cost_center,
        date_format: userData?.date_format,
        time_format: userData?.time_format,
        locked_out: userData?.locked_out,
        zip: userData?.zip,
        street: userData?.street,
      };
      user_data = { ...user_data, ...moreInfo };
    }
    return { ...user_data, lockedOut: user_data?.locked_out === "true" || !user_data?.user_name };
  } catch (err) {
    return {};
  }
};

export const stringToColor = (name: string) => {
  if (!name) return 'transparent';
  let hash = 0;
  let i;

  for (i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
};

export const stringAvatar = (name: string, size: number) => {
  const name_arr = name?.split(' ');
  return {
    sx: {
      bgcolor: stringToColor(!!name ? name : ''),
      width: size,
      height: size
    },
    children: name_arr?.length > 1? getAvatarName(name) : (name?.length > 2? name?.slice(0, 1)?.toUpperCase() : name), 
  }; 
};

export function getAvatarName(name: string) {
  const name_arr = !!name ? name.split(' ') : '';
  const avatar_name =
    name_arr?.length > 1
      ? `${name_arr[0][0]?.toUpperCase() || ''}${name_arr[1][0]?.toUpperCase() || ''}`
      : name_arr[0]?.slice(0, 1)?.toUpperCase();
  return avatar_name;
};

export const getCountryLabelByCode = (code: string, countryData: any) => {
  return countryData.find(function(element: any) {
    return element.value === code;
  })?.name;
};

export const getCountryCode = (addressObj: any, isTypePickupLocation: boolean) => {
  if (!addressObj || !addressObj.country) return '';
  return addressObj.country === 'chn' && !isTypePickupLocation
    ? `${addressObj.country}-${addressObj.states}`
    : addressObj.country;
};

export const generateAddressStrPickupLocation = (addressObj: any, contactObj: any) => {
  const { shipping_address } = addressObj;
  const contactInfo = `${contactObj.firstName} ${contactObj.lastName}, ${contactObj.phoneType}: ${isValidPhoneNumber(contactObj.phoneNumber) ? formatPhoneNumberIntl(contactObj.phoneNumber) : contactObj.phoneNumber}`;

  if (!addressObj || !contactObj) {
    return '';
  }

  return {
    fullAddress: `${contactInfo}, ${shipping_address}`,
    contactInfo: `${contactInfo}`,
    dropOffLocation: `${shipping_address}`
  };
};

export const generateAddressStr = (addressObj: any, contactObj: any, addressData: any) => {
  let result = '';
  if (!addressObj || !contactObj) {
    return result;
  }

  const { isPickUp, pickUpDepot, country, ...address } = addressObj;
  if (isPickUp) return result;

  const { countryList = [], dataField = {} } = addressData;
  const countryFields = dataField[country] || [];
  const controlList = countryFields.reduce((controlList: any[], obj: any) => {
    const controls = obj?.controls || [];
    return [...controlList, ...controls];
  }, []);

  const countryObj = countryList.find((c: any) => c.code === country) || {};
  const countryName = countryObj.name || country;

  const getAddressName = (key: string) => {
    const addressValue = address[key];

    const control = controlList.find((c: any) => c.key === key);
    if (!control || control.type !== 'comboBox') return addressValue;

    const selectedItem = control.data.find((i: any) => i.code === addressValue) || {};
    return selectedItem.name || addressValue;
  };

  Object.keys(address).forEach(key => {
    result += !!address[key] ? `, ${getAddressName(key)}` : '';
  });

  return (
    `${contactObj.firstName} ${contactObj.lastName}, ` +
    `${
      contactObj.phoneNumber
        ? contactObj.phoneType + ' : ' + contactObj.phoneNumber
        : ''
    }, ` + countryName + result
  );
};

export const generateBusinessJustification = (businessJustification: any, comments: any) => {
  return !!comments ? `${businessJustification}<br />${comments}` : businessJustification;
};

export function generateAuditLog(oldDelegation: any, newDelegation: any, template: any){
  type dictionaryKey = keyof typeof dictionaryKeyObj
	const dictionaryKeyObj = {
  	delegate: "USER",
    end_date: "END_DATE",
    start_date: "START_DATE",
    approvals: "ENABLE",
  }
  const auditLog = [];
	for(let key in oldDelegation){
    if(newDelegation[key] !== oldDelegation[key]){
      const newKey = {...template}
      newKey['old_value'] = oldDelegation[key]
      newKey['new_value'] = newDelegation[key]
      newKey['action'] = dictionaryKeyObj[key as dictionaryKey]
      auditLog.push(newKey);
    }
  }
  if(auditLog.length > 0){
    switch(auditLog[0]?.action) {
      case "USER":
        if(auditLog[0]?.old_value) return 'UPDATE_DELEGATION';
        else return "DEFINE_DELEGATION";
      case "ENABLE":
        if(auditLog[0]?.old_value === false && auditLog[0]?.new_value === true)
        return "ENABLE_DELEGATION";
        else return "DISABLE_DELEGATION";
      case "END_DATE":
        return 'UPDATE_DELEGATION';
      case "START_DATE":
        return 'UPDATE_DELEGATION';
      default:
        return undefined;
    }
  }

	// if(auditLog.length > 1) return auditLog
	// return auditLog[0]
};

export const getKeyFeaturesConfig = (arr: any[], key: string) => {
  return arr?.find(item => item.key === key);
};

export const checkEnabledFeature = (obj: any) => {
  return !!obj && Object.keys(obj).length > 0 && obj?.enabled;
};

export const getPermission = async ({
  configKey = CONFIG.PERMISSION_ABO.LABEL,
  configLabel = CONFIG.PERMISSION.LABEL
}: {
  configKey: string;
  configLabel: string;
}) => {

  try {
    let res = await AppService.getValueConfigAzure(configKey, configLabel);
    res = res ? res : {};
    if(res?.[CONFIG.KNOWLEDGE.KNOWLEDGE_KEY]?.features?.length === 0) {
      const resKnowledgeConfig = await AppService.getValueConfigAzure(configKey === CONFIG.PERMISSION.KEY? CONFIG.KNOWLEDGE.KNOWLEDGE_CONFIG.KEY : CONFIG.KNOWLEDGE.KNOWLEDGE_CONFIG_ABO.KEY, configLabel);
      res = {...res, [CONFIG.KNOWLEDGE.KNOWLEDGE_KEY]: {...res?.[CONFIG.KNOWLEDGE.KNOWLEDGE_KEY], ...resKnowledgeConfig?.[CONFIG.KNOWLEDGE.KNOWLEDGE_KEY]}};
      return res;
    } else {
      return res;
    }
  } catch {
    return {};
  }
};

export const getParamsNotificationMobile = (kbNumber: string, type: string, userUid: string, notificationConfig: Array<{
  title: string;
  subtitle: string;
  body: string}>
  ) => {
  const getConfigNotification = notificationConfig?.find((val: any) => val?.key === type);
  const params = {
    title: getConfigNotification?.title,
    subtitle: `${getConfigNotification?.subtitle} ${kbNumber}`,
    body: `${getConfigNotification?.body} ${kbNumber}`,
    notificationType: type,
    userID: userUid
  };
  return params
};

export const pushStep = (step: string, history: any, push?: boolean) => {
  if (!history) return;
  const stepName = queryString.stringify({
    step: step,
  });
  const url = `${history.location.pathname}?${stepName}`;
  const state = history.location.state;

  if(history.location.pathname !== NotFoundPath) {
    push ? history.push(url, state) : history.replace(url, state)
  };
};

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

export const getFirstLetters = (name: string) => {
  const words = name.split(" ");
  const firstLetters = words.map((word) => word[0]);
  const initials = firstLetters.splice(0, 2).join("").toUpperCase();
  return initials;
};

export const submitFiles = async (files: File[], RitmSysId: string) => {
  const filesError: string[] = [];
  await Promise.all(
    files.map(async file => {
      try {
        const params = {
          id: RitmSysId,
          name: file.name,
          type: file.type,
        };
        const result = await TicketServices.postAttachmentsRequest(
          params,
          file,
        );
        if (result.status !== 200 || !result.data?.success) {
          filesError.push(file.name);
        }
      } catch (error) {
        filesError.push(file.name);
        return;
      }
    }),
  );
  return filesError;
};

// mapping = { newKey: oldKey }
export const mapObject = (object: any, mapping: any = {}, takeRest?: boolean) => {
  if (Object.keys(mapping).length === 0) return object;

  const mappedObject: Record<string, any> = {};
  for (const newKey in mapping) {
    const oldKey = mapping[newKey];
    mappedObject[newKey] = object[oldKey];
  }

  return {
    ...(takeRest ? object : {}),
    ...mappedObject,
  };
};

export const mapArrayObject = (data: any, mapping: any = {}, takeRest: boolean = false) => {
  if (!data) return data;

  if (Array.isArray(data)) {
    return data.map(item => mapObject(item, mapping, takeRest));
  }

  return mapObject(data);
};

export function convertSnakeToCamelCase(snakeCase: string) {
  if (snakeCase) {
    return snakeCase.toLowerCase().replace(/(_\w)/g, match => match[1].toUpperCase());
  }
  return snakeCase;
};

export function convertTextToCamelCase(sentence: string) {
  if (sentence) {
    const words = sentence.split(' ');
    const camelWords = words.map((word, index) =>
      index === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    );
    return camelWords.join('');
  }
  return sentence;
};

export const getContactCardInfo = (autoPopulate: boolean, pclName: string, dataPCL: {contactData: any, userData: any, someone?: any, isForYourSelf?: boolean}) => {
  if ([PC_PROCESSING.PC_ORDER].includes(pclName)) {
    return {
      firstName: !!dataPCL.contactData?.firstName ?
        dataPCL.contactData?.firstName
        :
        (dataPCL.isForYourSelf ? dataPCL.userData.first_name : dataPCL.someone?.first_name),

      lastName: !!dataPCL.contactData?.lastName ?
        dataPCL.contactData?.lastName
        :
        (dataPCL.isForYourSelf ? dataPCL.userData.last_name : dataPCL.someone?.last_name),

      phoneType: !!dataPCL.contactData?.phoneType ?
        dataPCL.contactData?.phoneType
        :
        autoPopulate ?
          (dataPCL.isForYourSelf ?
            (!!dataPCL.userData?.mobile_phone ? PHONE_TYPE.MOBILE : (!!dataPCL.userData?.phone ? PHONE_TYPE.WORK : PHONE_TYPE.HOME))
            :
            (!!dataPCL.someone?.mobile_phone ? PHONE_TYPE.MOBILE : (!!dataPCL.someone?.phone ? PHONE_TYPE.WORK : PHONE_TYPE.HOME))
          )
          : PHONE_TYPE.HOME,

      phoneNumber: !!dataPCL.contactData?.phoneNumber ?
        dataPCL.contactData?.phoneNumber
        :
        autoPopulate ?
          (dataPCL.isForYourSelf ?
            (!!dataPCL.userData?.mobile_phone ? dataPCL.userData?.mobile_phone : dataPCL.userData?.phone || '')
            :
            (!!dataPCL.someone?.mobile_phone ? dataPCL.someone?.mobile_phone : dataPCL.someone?.phone || '')
          )
          : ''
    }
  }

  return {
    firstName: dataPCL.contactData?.firstName || dataPCL.userData.first_name,

    lastName: dataPCL.contactData?.lastName || dataPCL.userData.last_name,

    phoneType: !!dataPCL.contactData?.phoneType ?
      dataPCL.contactData.phoneType
      :
      autoPopulate ?
        (!!dataPCL.userData?.mobile_phone ? PHONE_TYPE.MOBILE : (!!dataPCL.userData?.phone ? PHONE_TYPE.WORK : PHONE_TYPE.HOME))
        : PHONE_TYPE.HOME,

    phoneNumber: !!dataPCL.contactData?.phoneNumber ?
      dataPCL.contactData.phoneNumber
      :
      autoPopulate ?
        (!!dataPCL.userData?.mobile_phone ? dataPCL.userData?.mobile_phone : dataPCL.userData?.phone || '')
        : ''
  }
};

export const checkInTeams = () => {
  // eslint-disable-next-line dot-notation
  const microsoftTeamsLib = microsoftTeams || (window as any)['microsoftTeams'];

  if (!microsoftTeamsLib) {
    return false; // the Microsoft Teams library is for some reason not loaded
  }

  if (
    (window.parent === window.self && (window as any).nativeInterface) ||
    window.name === 'embedded-page-container' ||
    window.name === 'extension-tab-frame'
  ) {
    return true;
  }

  return false;
};

export const getSystemDateTimeFormat = (
  systemDateTimeFormat?: string,
): { date: string; time: string } | null => {
  // The systemDateTimeFormat have the format as: "Date Time"
  // i.e: "DD.MM.YYYY HH:mm:ss"
  if(systemDateTimeFormat){
    const splittedDateAndTime = systemDateTimeFormat.split(' '); // splittedDateAndTime = [Date, Time]
    const date = splittedDateAndTime[0];
    const time = splittedDateAndTime[1];
    return {
      date,
      time,
    };
  }
  return null;
};

export const handleNavigateStaticTab = (teamsConfig: any) => {
  microsoftTeams.app.initialize().then((res) => {
    const { pages } = microsoftTeams;
    const { pageId, appId } = teamsConfig;

    if (pages.currentApp.isSupported()) {
      const navPromise = pages.currentApp.navigateTo({ pageId });
      navPromise.then((result) => {}).catch((error) => {console.error(error)});
    }
    else {
        const navPromise = pages.navigateToApp({ appId, pageId });
        navPromise.then((result) => {}).catch((error) => {console.error(error)});
    }
  });
};

export const handleValidateSoftware = async (payload: {
  email: string;
  sku: string;
  userSysId: string;
  requestType?: string;
}) => {
  try {
    const res = await ProvisioningServices.checkValidateSoftware(payload);
    return res;
  } catch (error) {
    console.error(error);
  }
};


export const findNewJoinerByOrderId = async (orderId: any, email: string) => {
  try {
    const res = await OnBoardingService.getNewJoiners(email, 1, 1000);

    const findNewJoiner = res?.data?.data.find((user: any) => user.latestOrder._id === orderId);

    if(!!findNewJoiner) return findNewJoiner.id;
  } catch (err) {
    console.error(err);
  }
};

export const updateNewJoinerOrderDeviceTask = async (newjoiner_id: string, status: string, orderTaskDefinitionId: string) => {
  try {
    const res = await OnBoardingService.getNewJoinerTasks(newjoiner_id);

    if(!!res) {
      const findDeviceTask = res?.data?.find((task: any) => task.task_definition_id === orderTaskDefinitionId);
      if(!!findDeviceTask) await OnBoardingService.updateNewJoinerTasks([{ id: findDeviceTask.id, status }]);
    }
  } catch (err) {
    console.error(err);
  }
};

export const formatLabelButton = (text: string, textTransform: string) => {
  let label;
  switch (textTransform) {
    case 'lowercase':
      label = text?.toLowerCase();
      break;
    case 'uppercase':
      label = text?.toUpperCase();
      break;
    case 'capitalize':
      label = text?.charAt(0)?.toUpperCase() + text?.slice(1);
      break;
    default:
      label = text;
      break;
  }
  return label;
};

export const matchingRoutePath = (url: string, path: string) => {
  return matchPath(url, {
    path: path,
    exact: true,
    strict: false
  });
};

export const handleSearchParams = (search: string) => {
  const searchParams = new URLSearchParams(search);
  const ticketNumber: string | null = searchParams.get('ticketNumber') || searchParams.get('search');
  const type: string | null = searchParams.get('type');

  return { type: !!type ? `?type=${type}` : '', ticketNumber: !!ticketNumber ? `?ticketNumber=${ticketNumber}` : '' };
};

export const handleCustomUrl = (route: RouteConfig, data: any, path: string, ticketNumber: string, search:string = "" ) => {
  const hasParamsId = isEmpty(data?.params);

  if(!hasParamsId) {
    const paramName = Object.keys(data.params)[0];
    const paramValue = data.params[paramName];
    const reg = /\*/g;
    return {...route, path: reg.test(path) ? `${path.replace(reg,paramValue)}${search}` : `${path?.replace(`:${paramName}`, paramValue)}${search}`};
  } else if (!!ticketNumber) {
    return {...route, path: `${path}${ticketNumber}`};
  } else {
    return {...route, path: `${path}${search}`};
  };
};

export const getPernmalink = (routes: InternalRouteProps[], url: string, search?: string) => {
  const data = routes?.map((route: InternalRouteProps) => {
    const { internalPath, permalinkPath }: any = route;
    
    const matchingData:any = matchingRoutePath(url, internalPath);
    
    const hasParamsId = isEmpty(matchingData?.params);
    if(!hasParamsId) {
      const paramName = Object.keys(matchingData.params)[0];
      const paramValue =  matchingData.params[paramName];
      const reg = /\*/g;
      return reg.test(permalinkPath) ? `${permalinkPath?.replace(reg, paramValue)}${search}` :`${permalinkPath?.replace(`:${paramName}`, paramValue)}${search}`;
    }else if(!!matchingData){
      return `${permalinkPath}${search}`;
    }

    return null;
  }).filter(Boolean);

  return data;
};

export const findRoute = (routes: RouteConfig[], url: string, search: string) => {
  const { type, ticketNumber }: any = handleSearchParams(search);

  const data = routes?.map((route: RouteConfig) => {
    const { permalinkPath, path, oldPath }: any = route;

    const isPermalinkUrl = matchingRoutePath(url, permalinkPath);

    const isOldPathUrl = oldPath?.map((oldPathUrl: string) => {
      if(!!type && oldPathUrl.includes(type)) {
        return matchingRoutePath(`${url}${type}`, oldPathUrl);
      };
      return matchingRoutePath(url, oldPathUrl);
    }).filter(Boolean);

    if(!!isPermalinkUrl) {
      const data = handleCustomUrl(route, isPermalinkUrl, path, ticketNumber, search);
      return {...data, trackingPath: {url: `${window.location.origin}${isPermalinkUrl.url}` , fromPermalinkUrl: true}};
    };

    if (!!isOldPathUrl && isOldPathUrl?.length > 0) {
      const data =  handleCustomUrl(route, isOldPathUrl[0], path, ticketNumber, search);
      return {...data, trackingPath: {url: `${window.location.origin}${isOldPathUrl[0].url}`, fromPermalinkUrl: false}};
    };

    return null;
  }).filter(Boolean);

  return data;
};

export const handleCheckCustomRoutes = (routes: RouteConfig[]) => {
  const { search, pathname: url } = window.location;
  const customRoute = routes?.filter((item: any) => item?.permalinkPath);
  let newPath: any = '';
  let trackingPath: any = null;

  if (customRoute?.length > 0) {
    const res = findRoute(customRoute, url, search);
    newPath =  !!res && res.length > 0 ? res[0]?.path : NotFoundPath;
    trackingPath = !!res && res.length > 0 ? res[0]?.trackingPath : null;
  };

  const result: CheckRoutesConfigProps = {
    hasNewRoutesPath: !!newPath,
    newPath: newPath || `${url}${search}`,
    trackingPath: trackingPath
  };


  return result;
};

export const saveFile = (fileData: {
  content: Blob;
  name: string;
}) => {
  const { content, name } = fileData;
  const anchor = document.createElement('a');
  const url = URL.createObjectURL(content);

  anchor.href = url;
  anchor.download = name;
  anchor.click();

  URL.revokeObjectURL(url);
};

export const handleListQuickLinks = (dataQuickLinks: any) => {
  const platform = (window.navigator as any).userAgentData?.platform;
  const newData = dataQuickLinks?.features
    ?.filter((item: any) => item.enabled)
    ?.filter(
      (item: any) =>
        item?.enabledPlatform?.find((i: any) => i?.platform === platform)
          ?.platform,
    );
  return newData;
};

export const getDeviceDTO = (deviceDetail: IDevice) => {
  return DateUtils.formatDateOfDeviceConfig({
    ...deviceDetail,
    configuration: {
      since: deviceDetail?.assigned_date || 'N/A',
      lastActive: deviceDetail?.last_discovered,
    },
    manufacturer: deviceDetail['model.manufacturer'],
  });
};

export const getStaticLandingPageData = (
  arr: any[],
  key: string,
  pageName: string,
) => {
  const { ...data } = arr.find((item: any) => item[key])[key];
  const { enabled, content, backgroundImage } = data?.children?.find(
    (item: any) => item.key === pageName,
  );
  return {
    overline: data?.overline,
    header: data?.header,
    enabled,
    desc: content,
    backgroundImage,
  };
};

export const mergeObject = <TObject, TSource>(object: TObject, source: TSource) => {
  const cloneObject = JSON.parse(JSON.stringify(object) || '{}');
  return mergeWith(cloneObject, source, (value, srcValue) => {
    if (!srcValue) {
      return value;
    }
  });
};

export const checkValidInputEmail = (emailValue: string): boolean => {
  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(emailValue);
};

export const checkAcceptedEmailDomains = (email: string, allowedDomains: string[]) => {
  const domains = allowedDomains.join('|');
  const regexAcceptedEmail = `[a-zA-Z0-9]{0,}([.]?[a-zA-Z0-9]{1,})[@](${domains})$`;
  const regex = new RegExp(regexAcceptedEmail);
  return regex.test(email);
};

export const validateEmail = (email: string, options: { acceptedEmail?: string[] } = {}): [string, boolean] => {
  const { acceptedEmail = [] } = options;
  const isPersonalEmail = acceptedEmail.length === 0;
  const invalidEmailInputMessage = EMAIL_VALIDATION_ERROR_CODE?.[isPersonalEmail ? 'INVALID_PERSONAL_EMAIL' : 'INVALID_EMAIL_DOMAIN'];

  if (email.includes(' ')) return [invalidEmailInputMessage, false];

  const emailLowerCase = email.toLowerCase().trim();
  const isValidEmail = checkValidInputEmail(emailLowerCase);
  if (!isValidEmail) return [EMAIL_VALIDATION_ERROR_CODE.INVALID_EMAIL, false];

  if (acceptedEmail.length === 0) return ['', true];

  const isValid = checkAcceptedEmailDomains(emailLowerCase, acceptedEmail);
  const errorCode = isValid ? '' : EMAIL_VALIDATION_ERROR_CODE.INVALID_EMAIL_DOMAIN;
  return [errorCode, isValid];
};

export const handleCheckLastDate = (res: any, type: string) => {
  try {
    const currentDate = new Date();
    // const temp= moment().add(1,'day').toDate();
    if(type === 'last_working_date'){
      if (!res || !res?.last_working_date) {
        return false;
      };
  
      const lastWorkingDate = new Date(res.last_working_date);
      return lastWorkingDate < currentDate;
    }

    else if(type === 'last_access_date'){
      if (!res || !res?.last_access_date) {
        return false;
      };
  
      const lastAccessDate = new Date(res.last_access_date);
      return lastAccessDate < currentDate;
    }
    else return false; 
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const getKbDate = (kbDetail: any, isPublishedDate: boolean) => {
  const date = isPublishedDate
    ? kbDetail?.publishedDate
    : kbDetail?.lastUpdated || kbDetail?.publishedDate;
  return DateUtils.timetoXbyGMT(date);
};

export const handlePatternURL = (value: any[], href: string, history: any) => {
  const res = value?.find((item: any) =>
    Array.isArray(item?.patternDirect)
      ? item.patternDirect.some((url: string) => href?.includes(url))
      : href?.includes(item?.patternDirect),
  );
  
  if (!!res) {
    const objURL = new URL(href);
    const directURL = `${objURL.pathname}${objURL.search}`;
    history.push(directURL);
  } else {
    window.open(href, '_blank');
  }
};

export const scrollTicketDetailToBottom = () => {
  setTimeout(() => {
    let inner: any = document?.getElementsByClassName('inner');
    if (inner.length > 0) {
      inner?.[0]?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  }, 50);
};

export const checkValidURL = (str: string) => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',// fragment locator
  ); 
  return !!pattern.test(str);
};

export const mappingComponentProps = (dynamicFormJson: any, pclConfig: any) => {
  if (!pclConfig?.componentPropsPCL) return dynamicFormJson;

  return Object.fromEntries(
    Object.entries(dynamicFormJson).map(([key, steps]: [string, any]) => {
      const mappingSteps = (steps?.items || []).map((item: any) => {
        const matchingComponent = pclConfig?.componentPropsPCL?.find((component: any) => item.key === component.key);
        if (matchingComponent) {
          item = { ...item, componentProps: { ...item.componentProps, ...matchingComponent.componentProps } };
        }
        return item;
      });
      return [key, { ...steps, items: mappingSteps }];
    })
  );
};

export const validateRequiredFieldsEmpty = (requiredFields: any, formData: any) => {
  if (formData.isPickUp && formData.pickUpDepot) return false;
  for (const field in requiredFields) {
    if ((requiredFields[field] && !formData[field])) {
      return true;
    }
  }
  return false;
};

export const handleKeyPress = (
  event: KeyboardEvent | React.KeyboardEvent<HTMLButtonElement>,
  key: string,
  func: any,
  thisArgs: any = null,
  ...args: any[]
) => {
  if (event.key === key) {
    func.call(thisArgs, args);
  }
};

/**
 * Checks if the user's country is supported.
 *
 * @param {Object[]} unsupportedCountryData - Array of unsupported country objects.
 * @param {string} userCountryCode - The country code of the user.
 * @returns {boolean} - Returns false if the user's country is unsupported, otherwise true.
 */

export const checkSupportedCountry = (unsupportedCountryData: {name: string; code: string}[], userCountryCode: string) => {
  if (unsupportedCountryData.length > 0) {

    const isUnSupportedCountry = unsupportedCountryData.some(
      (country) => country?.code?.toUpperCase() === userCountryCode?.toUpperCase()
    );

    // If the user's country is unsupported, return false; otherwise, return true
    return !isUnSupportedCountry;
  }

  return true
};

export const sortCountriesByName = (countryList: ICountryProps[], defaultCode: string = 'usa') => {
  if (!Array.isArray(countryList) || countryList.length === 0) return [];
  const newList = [...countryList];

  newList.sort((a, b) => {
    const nameA = !!a.name && a.name.toUpperCase();
    const nameB = !!b.name && b.name.toUpperCase();
    return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
  });

  let index = newList.findIndex(item => item.code === defaultCode?.toLowerCase());
  if (index < 0) index = newList.findIndex(item => item.code === 'usa');
  const defaultItem = newList.splice(index, 1);
  newList.unshift(...defaultItem);

  return newList;
};

export const eyeBrowBackgroundColor = (props: any) => {
  const { item, field, isPickup, theme } = props;
  if (!item || !item[field]) {
    return theme.uptimeColors.mainTheme.light;
  }

  switch (isPickup ? item[field] : item[field].value) {
    case EXCEPTION_TYPES.STANDARD:
      return theme.uptimeColors.deviceCardEyebrow.inUse.background;

    case EXCEPTION_TYPES.EXCEPTION_REQUIRED:
    case EXCEPTION_TYPES.PREMIUM:
      return theme.uptimeColors.deviceCardEyebrow.exception.background;

    default:
      return theme.uptimeColors.mainTheme.light;
  }
};

export const eyeBrowColor = (props: any) => {
  const { item, field, isPickup, theme } = props;
  if (!item || !item[field]) {
    return theme.uptimeColors.mainTheme.primary;
  }

  switch (isPickup ? item[field] : item[field].value) {
    case EXCEPTION_TYPES.STANDARD:
      return theme.uptimeColors.deviceCardEyebrow.inUse.color;

    case EXCEPTION_TYPES.EXCEPTION_REQUIRED:
    case EXCEPTION_TYPES.PREMIUM:
      return theme.uptimeColors.deviceCardEyebrow.exception.color;

    default:
      return theme.uptimeColors.mainTheme.primary;
  }
};

export const exceptionLabel = (props: any) => {
  const { item, field, isPickup, locale } = props;

  if (!item || !item[field]) {
    return '';
  }

  switch (isPickup ? item[field] : item[field].value) {
    case EXCEPTION_TYPES.RECOMMENDED:
    case EXCEPTION_TYPES.EXCEPTION_REQUIRED:
    case EXCEPTION_TYPES.PREMIUM:
    case EXCEPTION_TYPES.STANDARD:
      if (isPickup) {
        return locale?.[item[field]?.toUpperCase()];
      } else {
        return locale?.[item[field]?.value?.toUpperCase()];
      }
    default:
      return '';
  }
};

export const checkApplyCustomLayout = (key: string) => {
  const applyCustomLayoutConfig = JSON.parse(sessionStorage.getItem(AUTH.CONFIGURATION) || '' )?.applyCustomLayout ?? {};
  let isApplyConfigKey = false;
  if(Object.hasOwn(applyCustomLayoutConfig, key)) {
    isApplyConfigKey = applyCustomLayoutConfig[key] ?? false;
  }
  return isApplyConfigKey;
};

export const convertAlpha3ToAlpha2 = (alpha3: string) => {
  if (!alpha3) return '';
  return byAlpha3[alpha3]?.alpha2 || '';
};

export const normalizeWhitespace = (text: string) => {
  return text.trim().replace(/\s+/g, ' ');
};